/**
 * Bundle "Case Only" override
 *
 * Reads a Liquid-rendered JSON map from [data-case-only-map] and:
 *   1. Injects a "Case Only" option into the Cushion Type dropdown
 *   2. When that option is selected, swaps the form submission to add
 *      the standalone case product variant instead of the bundle.
 *
 * No "Case Only" variant is needed on the bundle product itself —
 * the option is injected entirely client-side.
 *
 * All product / variant data is resolved server-side in Liquid —
 * this script does zero network requests for product data.
 */
(function () {
  'use strict';

  /* ── Bootstrap ────────────────────────────────────────────────── */
  var el = document.querySelector('[data-case-only-map]');
  if (!el) return;

  var config;
  try { config = JSON.parse(el.textContent); }
  catch (e) { return; }

  var map           = config.variants;
  var materialPos   = config.materialOptionPosition;
  var designPos     = config.designOptionPosition;
  var form          = document.querySelector('[data-product-form]');
  if (!form || !map) return;

  var CASE_ONLY     = config.caseOnlyLabel || 'Case Only';
  var isCaseOnly    = false;
  var caseVariantId = null;

  /* ── Inject "Case Only" into the Cushion Type dropdown ─────── */

  var materialSelect = form.querySelector('select#option' + materialPos);
  if (materialSelect) {
    // Only inject if not already present (in case the bundle still
    // carries a real "Case Only" variant from before migration)
    var alreadyExists = Array.prototype.some.call(
      materialSelect.options,
      function (o) { return o.value === CASE_ONLY; }
    );
    if (!alreadyExists) {
      var opt = document.createElement('option');
      opt.value = CASE_ONLY;
      opt.textContent = CASE_ONLY;
      materialSelect.appendChild(opt);
    }
  }

  /* ── DOM refs ─────────────────────────────────────────────────── */

  var addBtn        = form.querySelector('[data-add-to-cart]');
  var addBtnText    = addBtn && addBtn.querySelector('[data-add-to-cart-text]');
  var savedBtnText  = addBtnText ? addBtnText.textContent : 'Add to cart';

  /**
   * Read the current value of a product option by its Shopify position.
   */
  function readOption(position) {
    var sel = form.querySelector('select#option' + position);
    if (sel) return sel.value;

    var wrap = form.querySelector('[data-product-option="Option' + position + '"]');
    if (wrap) {
      var active = wrap.querySelector('.selected');
      if (active) return active.getAttribute('aria-label');
    }
    return null;
  }

  /* ── State check ──────────────────────────────────────────────── */

  function check() {
    var materialVal = readOption(materialPos);
    isCaseOnly = (materialVal === CASE_ONLY);

    if (isCaseOnly) {
      var entry;
      if (designPos) {
        var designVal = readOption(designPos);
        entry = designVal && map[designVal];
      } else {
        entry = map['_default'];
      }
      caseVariantId = entry ? entry.id : null;
      updatePrice(entry);
      fixButton(entry);
      fixSwatches();
    } else {
      caseVariantId = null;
    }

    // Hide / show dynamic checkout (Shop Pay etc.)
    var dc = form.querySelector('.shopify-payment-button');
    if (dc) dc.style.display = isCaseOnly ? 'none' : '';
  }

  /* ── Fix design swatches ────────────────────────────────────── */
  // The theme marks all design swatches as disabled/sold-out when
  // "Case Only" is selected because no bundle variant matches it.
  // Override swatch states using the case product's availability.

  function fixSwatches() {
    if (!designPos) return;
    var group = form.querySelector(
      '[dynamic-variant-input-wrap][data-index="option' + designPos + '"]'
    );
    if (!group) return;

    Object.keys(map).forEach(function (designValue) {
      if (designValue === '_default') return;
      var escaped = designValue.replace(/"/g, '\\"').replace(/'/g, "\\'");
      var btn = group.querySelector(
        '[dynamic-variant-button][data-option-value="' + escaped + '"]'
      );
      if (!btn) return;

      if (map[designValue].available) {
        btn.classList.remove('disabled');
      } else {
        btn.classList.add('disabled');
      }
    });
  }

  /* ── Fix add-to-cart button ─────────────────────────────────── */
  // The theme disables the button when its variant lookup returns
  // null (which happens for our injected "Case Only" value).  We
  // re-enable it and restore the label.

  function fixButton(entry) {
    if (!addBtn) return;

    if (entry && entry.available) {
      addBtn.disabled = false;
      addBtn.removeAttribute('aria-disabled');
      if (addBtnText) addBtnText.textContent = savedBtnText;
    } else if (entry && !entry.available) {
      addBtn.disabled = true;
      if (addBtnText) addBtnText.textContent = 'Sold out';
    }
  }

  /* ── Money formatting ────────────────────────────────────────── */
  // Handles all four Shopify money format placeholders so prices
  // render correctly in every market (EU uses comma separators).

  function formatMoney(cents) {
    var fmt = window.theme && window.theme.moneyFormat
                ? window.theme.moneyFormat : '${{amount}}';
    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return cents;

    var number = cents / 100;
    var value;

    switch (match[1]) {
      case 'amount':
        value = number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
        break;
      case 'amount_no_decimals':
        value = Math.round(number).toString().replace(/\d(?=(\d{3})+$)/g, '$&,');
        break;
      case 'amount_with_comma_separator':
        value = number.toFixed(2)
          .replace(/\d(?=(\d{3})+\.)/g, '$&.')
          .replace(/\.(\d{2})$/, ',$1');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = Math.round(number).toString().replace(/\d(?=(\d{3})+$)/g, '$&.');
        break;
      default:
        value = number.toFixed(2);
    }

    return fmt.replace(match[0], value);
  }

  /* ── Price display ────────────────────────────────────────────── */

  function updatePrice(entry) {
    if (!entry) return;
    var priceEl   = document.querySelector('[data-product-price]');
    var compareEl = document.querySelector('[data-compare-price]');

    if (priceEl) {
      priceEl.textContent = formatMoney(entry.price);
    }
    if (compareEl) {
      if (entry.compareAtPrice && entry.compareAtPrice > entry.price) {
        compareEl.textContent = formatMoney(entry.compareAtPrice);
        compareEl.classList.remove('hide');
      } else {
        compareEl.classList.add('hide');
      }
    }
  }

  /* ── Listeners ────────────────────────────────────────────────── */

  document.addEventListener('flu:product:variant-change', function () {
    setTimeout(check, 20);
  });
  form.addEventListener('change', function () {
    setTimeout(check, 20);
  });

  // Catch async injection of dynamic-checkout button
  new MutationObserver(function () {
    var dc = form.querySelector('.shopify-payment-button');
    if (dc) dc.style.display = isCaseOnly ? 'none' : '';

    // Theme may re-disable the button after our check() runs
    if (isCaseOnly && addBtn && addBtn.disabled && caseVariantId) {
      addBtn.disabled = false;
      addBtn.removeAttribute('aria-disabled');
      if (addBtnText) addBtnText.textContent = savedBtnText;
    }
  }).observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });

  /* ── Submit intercept ─────────────────────────────────────────── */
  // We do our own fetch to /cart/add.js with the case variant ID,
  // then dispatch the theme's "apps:product-added-to-cart" event.
  // The theme listens for this event and calls cartUpdatedExternally()
  // which fetches fresh cart data, updates internal state, and opens
  // the cart drawer with the correct items — exactly like a normal
  // add-to-cart.

  form.addEventListener('submit', function (e) {
    if (!isCaseOnly || !caseVariantId) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (addBtn) addBtn.classList.add('bttn--loading');

    var qty = form.querySelector('[name="quantity"]');
    var body = 'id=' + caseVariantId + '&quantity=' + (qty ? qty.value : 1);

    // Include visible properties but skip private ones (prefixed _)
    form.querySelectorAll('[name^="properties"]').forEach(function (inp) {
      if (inp.name.indexOf('[_') === -1) {
        body += '&' + encodeURIComponent(inp.name) + '=' + encodeURIComponent(inp.value);
      }
    });

    var url = (window.theme && window.theme.routes && window.theme.routes.cart
                ? window.theme.routes.cart.add : '/cart/add') + '.js';

    fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: body
    })
    .then(function (r) { return r.json(); })
    .then(function (item) {
      if (addBtn) addBtn.classList.remove('bttn--loading');

      if (item.status == 422) {
        var qErr = form.querySelector('[data-quantity-error]');
        if (qErr) { qErr.textContent = item.description; qErr.classList.remove('hidden'); }
        return;
      }

      // Tell the theme a product was added externally.
      // This triggers cartUpdatedExternally() → fetchCart → state update
      // → cart:updated → drawer opens with fresh data + cart count updates
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
    })
    .catch(function () {
      if (addBtn) addBtn.classList.remove('bttn--loading');
    });
  }, true);

  // Initial check
  check();
})();
